import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import { Settings2, GripVertical, Eye, EyeOff, Plus, SlidersHorizontal } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ column, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-background border rounded-lg mb-2 select-none ${
        isDragging ? 'opacity-40 z-50 shadow-lg' : 'opacity-100'
      } hover:bg-muted/50`}
    >
      <div className="flex items-center gap-3">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded text-muted-foreground hover:text-primary touch-none"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <div>
          <span className="font-medium">{column.label}</span>
          {column.isCustomField && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
              custom
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onToggleVisibility(column.id)}
        className="p-1 hover:bg-muted rounded"
      >
        {column.visible ? (
          <Eye className="h-4 w-4 text-green-600" />
        ) : (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};

export const ColumnCustomizer = ({
  columns,
  storageKey,
  onColumnsChange,
  extraColumns = [],
  onCreateCustomField,
  isAdmin = false,
  triggerClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const [localColumns, setLocalColumns] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCf, setNewCf] = useState({ label: '', field_type: 'texto', options: '' });
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Build full column list from standard + custom field columns
  const buildAllColumns = (stdCols, extraCols) => [
    ...stdCols,
    ...(extraCols || []).map((cf, idx) => ({
      id: `cf_${cf.id}`,
      label: cf.label,
      visible: true,
      order: stdCols.length + idx,
      isCustomField: true,
    })),
  ];

  // Merge saved preferences from localStorage with the current full column list.
  // Runs whenever the number of standard or custom columns changes.
  useEffect(() => {
    const allCols = buildAllColumns(columns, extraColumns);
    const saved = localStorage.getItem(`columns_${storageKey}`);
    if (saved) {
      try {
        const savedCols = JSON.parse(saved);
        const merged = allCols.map(col => {
          const s = savedCols.find(sc => sc.id === col.id);
          return s ? { ...col, visible: s.visible, order: s.order } : col;
        });
        merged.sort((a, b) => (a.order || 0) - (b.order || 0));
        setLocalColumns(merged);
        onColumnsChange(merged);
      } catch {
        setLocalColumns(allCols);
        onColumnsChange(allCols);
      }
    } else {
      setLocalColumns(allCols);
      onColumnsChange(allCols);
    }
  // Re-run when the count of standard or extra columns changes (new field added)
  }, [storageKey, columns.length, extraColumns.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setLocalColumns(prev => {
        const oldIndex = prev.findIndex(c => c.id === active.id);
        const newIndex = prev.findIndex(c => c.id === over.id);
        return arrayMove(prev, oldIndex, newIndex).map((item, idx) => ({ ...item, order: idx }));
      });
    }
  };

  const handleToggleVisibility = (id) => {
    setLocalColumns(prev =>
      prev.map(col => col.id === id ? { ...col, visible: !col.visible } : col)
    );
  };

  const handleSave = () => {
    localStorage.setItem(`columns_${storageKey}`, JSON.stringify(localColumns));
    onColumnsChange(localColumns);
    setOpen(false);
  };

  const handleReset = () => {
    const allCols = buildAllColumns(columns, extraColumns);
    const reset = allCols.map((col, idx) => ({ ...col, visible: true, order: idx }));
    setLocalColumns(reset);
    localStorage.removeItem(`columns_${storageKey}`);
    onColumnsChange(reset);
  };

  const handleShowAll = () => setLocalColumns(prev => prev.map(col => ({ ...col, visible: true })));
  const handleHideAll = () => setLocalColumns(prev => prev.map(col => ({ ...col, visible: false })));

  const handleCreateCf = async () => {
    if (!newCf.label.trim()) return;
    setCreating(true);
    try {
      const payload = {
        label: newCf.label.trim(),
        field_type: newCf.field_type,
        options: newCf.field_type === 'lista'
          ? newCf.options.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await onCreateCustomField(payload);
      setNewCf({ label: '', field_type: 'texto', options: '' });
      setShowCreateForm(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={triggerClassName} data-testid="customize-columns-btn">
          <Settings2 className="h-4 w-4 mr-2" />
          Personalizar columnas
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Personalizar Columnas</DialogTitle>
          <DialogDescription>
            Arrastra para reordenar y haz clic en el ojo para mostrar/ocultar columnas
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={handleShowAll}>
            <Eye className="h-3 w-3 mr-1" />
            Mostrar todas
          </Button>
          <Button variant="outline" size="sm" onClick={handleHideAll}>
            <EyeOff className="h-3 w-3 mr-1" />
            Ocultar todas
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Restablecer
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 min-h-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localColumns.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {localColumns.map(column => (
                <SortableItem
                  key={column.id}
                  column={column}
                  onToggleVisibility={handleToggleVisibility}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Sección para crear nuevo campo personalizado (solo admin) */}
          {isAdmin && onCreateCustomField && (
            <>
              <Separator className="my-3" />
              {!showCreateForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-dashed text-purple-700 border-purple-300 hover:bg-purple-50"
                  onClick={() => setShowCreateForm(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo campo personalizado
                </Button>
              ) : (
                <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      <SlidersHorizontal className="h-4 w-4 text-purple-600" />
                      Nuevo campo
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
                      Cancelar
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Etiqueta visible</Label>
                    <Input
                      value={newCf.label}
                      onChange={e => setNewCf(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Ej: Nivel de interés"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de campo</Label>
                    <Select
                      value={newCf.field_type}
                      onValueChange={v => setNewCf(prev => ({ ...prev, field_type: v }))}
                    >
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="texto">Texto</SelectItem>
                        <SelectItem value="numero">Número</SelectItem>
                        <SelectItem value="fecha">Fecha</SelectItem>
                        <SelectItem value="booleano">Sí / No</SelectItem>
                        <SelectItem value="lista">Lista de opciones</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newCf.field_type === 'lista' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Opciones (una por línea)</Label>
                      <Textarea
                        value={newCf.options}
                        onChange={e => setNewCf(prev => ({ ...prev, options: e.target.value }))}
                        placeholder={"Bajo\nMedio\nAlto"}
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                  )}
                  <Button
                    size="sm"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={handleCreateCf}
                    disabled={creating || !newCf.label.trim()}
                  >
                    {creating ? 'Creando…' : 'Crear campo'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-[#1a365d] hover:bg-[#1a365d]/90">
            Guardar preferencias
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ColumnCustomizer;
